import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { getAllSections } from '../data/newCurriculum';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Flame, Heart, Star, Trophy, Settings, BookOpen, 
  ChevronRight, Lock, CheckCircle2, Zap, Target,
  LogOut, User
} from 'lucide-react';
import axios from 'axios';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DuolingoHome() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, progressRes] = await Promise.all([
        axios.get(`${API}/gamification/program-stats`, { withCredentials: true }),
        axios.get(`${API}/program/progress`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error:', error);
      setStats({ xp: 0, streak: 0, level: 1, daily_goal_minutes: 10, today_minutes: 0 });
      setProgress({ overall: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.log('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
    navigate('/');
  };

  const sections = getAllSections();
  const dailyProgress = stats ? Math.min((stats.today_minutes / stats.daily_goal_minutes) * 100, 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#131F24] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-bounce text-6xl mb-4">✈️</div>
          <p className="text-white font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#131F24]">
      {/* Header */}
      <div className="bg-[#1A2C33] border-b border-gray-700/50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <span className="text-3xl">✈️</span>
              <span className="text-white font-bold text-xl">Cabin Crew Training</span>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-4">
              {/* Streak */}
              <div className="flex items-center gap-1">
                <Flame className="w-6 h-6 text-orange-500" />
                <span className="text-orange-500 font-bold">{stats?.streak || 0}</span>
              </div>
              
              {/* Mil */}
              <div className="flex items-center gap-1">
                <Zap className="w-6 h-6 text-yellow-500" />
                <span className="text-yellow-500 font-bold">{stats?.xp || 0}</span>
              </div>
              
              {/* Hearts */}
              <div className="flex items-center gap-1">
                <Heart className="w-6 h-6 text-red-500 fill-red-500" />
                <span className="text-red-500 font-bold">5</span>
              </div>
              
              {/* Profile */}
              <button 
                onClick={() => navigate('/profile')}
                className="p-2 hover:bg-gray-700 rounded-full transition-colors"
              >
                <User className="w-6 h-6 text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Welcome & Daily Goal */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            Merhaba, {user?.first_name || user?.name?.split(' ')[0] || 'Öğrenci'}! 👋
          </h1>
          
          {/* Daily Goal Card */}
          <Card className="bg-[#1A2C33] border-gray-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-500" />
                  <span className="text-white font-medium">Günlük Hedef</span>
                </div>
                <span className="text-green-400 font-bold">
                  {stats?.today_minutes || 0}/{stats?.daily_goal_minutes || 10} dk
                </span>
              </div>
              <Progress value={dailyProgress} className="h-3 bg-gray-700" />
              {dailyProgress >= 100 && (
                <p className="text-green-400 text-sm mt-2 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> Bugünkü hedefini tamamladın!
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Learning Path */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-400" />
            Öğrenme Yolu
          </h2>
          
          <div className="space-y-4">
            {sections.map((section, sectionIndex) => {
              const sectionProgress = progress[section.id];
              const progressPercent = sectionProgress?.completed 
                ? (sectionProgress.completed / sectionProgress.total) * 100 
                : 0;
              const isLocked = sectionIndex > 0 && progressPercent === 0;
              
              return (
                <Card 
                  key={section.id}
                  className={cn(
                    'border-0 transition-all cursor-pointer hover:scale-[1.02]',
                    isLocked ? 'bg-gray-800/50 opacity-60' : 'bg-[#1A2C33]'
                  )}
                  onClick={() => !isLocked && navigate(`/learn/${section.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div className={cn(
                        'w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-lg',
                        `bg-gradient-to-br ${section.color}`
                      )}>
                        {isLocked ? <Lock className="w-8 h-8 text-white/70" /> : section.icon}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="font-bold text-white text-lg">{section.title}</h3>
                        <p className="text-gray-400 text-sm">{section.subtitle}</p>
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{section.units.length} ünite</span>
                            <span>{Math.round(progressPercent)}%</span>
                          </div>
                          <Progress value={progressPercent} className="h-2 bg-gray-700" />
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className="w-6 h-6 text-gray-500" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <Card className="bg-[#1A2C33] border-0">
            <CardContent className="p-4 text-center">
              <Trophy className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <p className="text-2xl font-bold text-white">Seviye {stats?.level || 1}</p>
              <p className="text-xs text-gray-500">Seviye</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1A2C33] border-0">
            <CardContent className="p-4 text-center">
              <Star className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.xp || 0}</p>
              <p className="text-xs text-gray-500">Toplam Mil</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1A2C33] border-0">
            <CardContent className="p-4 text-center">
              <Flame className="w-8 h-8 mx-auto text-orange-500 mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.streak || 0}</p>
              <p className="text-xs text-gray-500">Gün Serisi</p>
            </CardContent>
          </Card>
        </div>

        {/* Logout Button */}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full text-gray-400 hover:text-white hover:bg-gray-800"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Çıkış Yap
        </Button>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#1A2C33] border-t border-gray-700/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-around py-3">
            <button className="flex flex-col items-center p-2 text-green-500">
              <BookOpen className="w-6 h-6" />
              <span className="text-xs mt-1">Öğren</span>
            </button>
            <button 
              onClick={() => navigate('/profile')}
              className="flex flex-col items-center p-2 text-gray-500 hover:text-gray-300"
            >
              <User className="w-6 h-6" />
              <span className="text-xs mt-1">Profil</span>
            </button>
            <button 
              onClick={() => navigate('/profile')}
              className="flex flex-col items-center p-2 text-gray-500 hover:text-gray-300"
            >
              <Settings className="w-6 h-6" />
              <span className="text-xs mt-1">Ayarlar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
